import { jwtDecode } from 'jwt-decode';
import { LocalSecureStorage } from '@/core/secure-storage';
import { TokenManager } from '@/core/auth/token-manager';
import { AuthApi } from '@/core/auth/auth-api';
import { AuthRepository } from '@/core/auth/data/auth-repository';
import { InitializeAuthUseCase } from '@/core/auth/use-cases/InitializeAuthUseCase';
import { LoginUseCase } from '@/core/auth/use-cases/LoginUseCase';
import { LoginWithTokenUseCase } from '@/core/auth/use-cases/LoginWithTokenUseCase';
import { LogoutUseCase } from '@/core/auth/use-cases/LogoutUseCase';
import { ActorApi } from '@/features/site-management/data/actor-api';
import { OperatorApi } from '@/features/operator-selection/data/operator-api';
import { KioskSessionApi } from '@/features/kiosk-sessions/data/kiosk-session-api';
import { ActorRepository } from '@/features/site-management/data/actor-repository';
import { OperatorRepository } from '@/features/operator-selection/data/operator-repository';
import { KioskSessionRepository } from '@/features/kiosk-sessions/data/kiosk-session-repository';
import { GetActorTreeUseCase } from '@/features/site-management/use-cases/GetActorTreeUseCase';
import { GetOperatorMachineListUseCase } from '@/features/site-management/use-cases/GetOperatorMachineListUseCase';
import { GetConnectorCountUseCase } from '@/features/site-management/use-cases/GetConnectorCountUseCase';
import { GetKioskSessionsUseCase } from '@/features/kiosk-sessions/use-cases/GetKioskSessionsUseCase';
// OCPI Admin API (unified, replaces ocpi-cpo and kiosk-operator)
import { OcpiAdminApi } from '@/features/ocpi-settings/data/ocpi-admin-api';
import { OcpiAdminRepository } from '@/features/ocpi-settings/data/ocpi-admin-repository';
import { CheckConnectionStatusUseCase } from '@/features/ocpi-settings/use-cases/CheckConnectionStatusUseCase';
import { PublishLocationsUseCase } from '@/features/ocpi-settings/use-cases/PublishLocationsUseCase';
import { PublishTariffsUseCase } from '@/features/ocpi-settings/use-cases/PublishTariffsUseCase';
import { SetupKioskConnectionUseCase } from '@/features/ocpi-settings/use-cases/SetupKioskConnectionUseCase';
import { OnboardOperatorUseCase } from '@/features/ocpi-settings/use-cases/OnboardOperatorUseCase';
import { SetupExternalCpoConnectionUseCase } from '@/features/ocpi-settings/use-cases/SetupExternalCpoConnectionUseCase';
import { SetupVpostConnectionUseCase } from '@/features/ocpi-settings/use-cases/SetupVpostConnectionUseCase';
import { GetCsmsNamesUseCase } from '@/features/ocpi-settings/use-cases/GetCsmsNamesUseCase';
import { GetKioskCsmsNamesUseCase } from '@/features/ocpi-settings/use-cases/GetKioskCsmsNamesUseCase';
import { SyncEvesUseCase } from '@/features/ocpi-settings/use-cases/SyncEvesUseCase';
import { SyncCpoOcpiDataUseCase } from '@/features/ocpi-settings/use-cases/SyncCpoOcpiDataUseCase';
// Manufacturer feature
import { ManufacturerApi } from '@/features/manufacturers/data/manufacturer-api';
import { ManufacturerRepository } from '@/features/manufacturers/data/manufacturer-repository';
import { GetManufacturersUseCase } from '@/features/manufacturers/use-cases/GetManufacturersUseCase';
import { GetManufacturerByIdUseCase } from '@/features/manufacturers/use-cases/GetManufacturerByIdUseCase';
import { CreateManufacturerUseCase } from '@/features/manufacturers/use-cases/CreateManufacturerUseCase';
import { UpdateManufacturerUseCase } from '@/features/manufacturers/use-cases/UpdateManufacturerUseCase';
import { DeleteManufacturerUseCase } from '@/features/manufacturers/use-cases/DeleteManufacturerUseCase';
// Sessions Report feature
import { SessionsReportApi } from '@/features/sessions-report/data/sessions-report-api';
import { SessionsReportRepository } from '@/features/sessions-report/data/sessions-report-repository';
import { GetSessionsUseCase } from '@/features/sessions-report/use-cases/GetSessionsUseCase';
import { GetMeterValuesUseCase } from '@/features/sessions-report/use-cases/GetMeterValuesUseCase';
import { GetTariffLevelsUseCase } from '@/features/sessions-report/use-cases/GetTariffLevelsUseCase';
// Operator Dashboard feature
import { DashboardApi } from '@/features/operator-dashboard/data/dashboard-api';
import { DashboardRepository } from '@/features/operator-dashboard/data/dashboard-repository';
import { GetFleetSummaryUseCase } from '@/features/operator-dashboard/use-cases/GetFleetSummaryUseCase';
import { GetFleetStatusUseCase } from '@/features/operator-dashboard/use-cases/GetFleetStatusUseCase';
import { GetFleetMapUseCase } from '@/features/operator-dashboard/use-cases/GetFleetMapUseCase';
import { GetAnalyticsUseCase } from '@/features/operator-dashboard/use-cases/GetAnalyticsUseCase';
import { GetActiveSessionsUseCase } from '@/features/operator-dashboard/use-cases/GetActiveSessionsUseCase';
import { GetRecentSessionsUseCase } from '@/features/operator-dashboard/use-cases/GetRecentSessionsUseCase';
import { GetAlertsUseCase } from '@/features/operator-dashboard/use-cases/GetAlertsUseCase';
import { GetAccountsUseCase } from '@/features/operator-dashboard/use-cases/GetAccountsUseCase';
// Translation Management feature
import { TranslationApi } from '@/features/translations/data/translation-api';
import { TranslationRepository } from '@/features/translations/data/translation-repository';
import { GetTranslationsUseCase } from '@/features/translations/use-cases/GetTranslationsUseCase';
import { GetAllTranslationsUseCase } from '@/features/translations/use-cases/GetAllTranslationsUseCase';
import { CreateTranslationUseCase } from '@/features/translations/use-cases/CreateTranslationUseCase';
import { UpdateTranslationUseCase } from '@/features/translations/use-cases/UpdateTranslationUseCase';
import { DeleteTranslationUseCase } from '@/features/translations/use-cases/DeleteTranslationUseCase';
import { GetLanguagesUseCase } from '@/features/translations/use-cases/GetLanguagesUseCase';
import { ValidateBulkImportUseCase } from '@/features/translations/use-cases/ValidateBulkImportUseCase';
import { BulkImportUseCase } from '@/features/translations/use-cases/BulkImportUseCase';
import { DownloadTemplateUseCase } from '@/features/translations/use-cases/DownloadTemplateUseCase';
import { ExportTranslationsUseCase } from '@/features/translations/use-cases/ExportTranslationsUseCase';
import { GetPublishStatusUseCase } from '@/features/translations/use-cases/GetPublishStatusUseCase';
import { PublishTranslationsUseCase } from '@/features/translations/use-cases/PublishTranslationsUseCase';
import { GetVersionsUseCase } from '@/features/translations/use-cases/GetVersionsUseCase';
import { GetVersionContentUseCase } from '@/features/translations/use-cases/GetVersionContentUseCase';
import { RollbackVersionUseCase } from '@/features/translations/use-cases/RollbackVersionUseCase';
import { GetPreviewInfoUseCase } from '@/features/translations/use-cases/GetPreviewInfoUseCase';
import { GetPreviewContentUseCase } from '@/features/translations/use-cases/GetPreviewContentUseCase';
import { DownloadPreviewUseCase } from '@/features/translations/use-cases/DownloadPreviewUseCase';
import { CopyLanguageValuesUseCase } from '@/features/translations/use-cases/CopyLanguageValuesUseCase';
// Language Management feature
import { LanguageManagementApi } from '@/features/translations/data/language-management-api';
import { LanguageManagementRepository } from '@/features/translations/data/language-management-repository';
import { GetAllLanguagesUseCase } from '@/features/translations/use-cases/GetAllLanguagesUseCase';
import { CreateLanguageUseCase } from '@/features/translations/use-cases/CreateLanguageUseCase';
import { UpdateLanguageUseCase } from '@/features/translations/use-cases/UpdateLanguageUseCase';
import { DeleteLanguageUseCase } from '@/features/translations/use-cases/DeleteLanguageUseCase';
import { GetApplicationLanguagesUseCase } from '@/features/translations/use-cases/GetApplicationLanguagesUseCase';
import { SetApplicationLanguagesUseCase } from '@/features/translations/use-cases/SetApplicationLanguagesUseCase';
import { BatchReorderLanguagesUseCase } from '@/features/translations/use-cases/BatchReorderLanguagesUseCase';
// Sound Management feature
import { SoundManagementApi } from '@/features/translations/data/sound-management-api';
import { SoundManagementRepository } from '@/features/translations/data/sound-management-repository';
import { GetSoundsUseCase } from '@/features/translations/use-cases/GetSoundsUseCase';
import { UploadSoundUseCase } from '@/features/translations/use-cases/UploadSoundUseCase';
import { DeleteSoundUseCase } from '@/features/translations/use-cases/DeleteSoundUseCase';
import { CopySoundsUseCase } from '@/features/translations/use-cases/CopySoundsUseCase';
// Import Configuration feature
import { ImportConfigurationApi } from '@/features/translations/data/import-configuration-api';
import { ImportConfigurationRepository } from '@/features/translations/data/import-configuration-repository';
import { GetSheetMappingsUseCase } from '@/features/translations/use-cases/GetSheetMappingsUseCase';
import { GetSheetMappingDetailUseCase } from '@/features/translations/use-cases/GetSheetMappingDetailUseCase';
import { SaveSheetMappingUseCase } from '@/features/translations/use-cases/SaveSheetMappingUseCase';
import { DeleteSheetMappingUseCase } from '@/features/translations/use-cases/DeleteSheetMappingUseCase';
import { SaveColumnMappingUseCase } from '@/features/translations/use-cases/SaveColumnMappingUseCase';
import { DeleteColumnMappingUseCase } from '@/features/translations/use-cases/DeleteColumnMappingUseCase';
// API Tokens feature
import { ApiTokensApi } from '@/features/api-tokens/data/api-tokens-api';
// Asset Management feature
import { AssetManagementApi } from '@/features/asset-management/data/asset-management-api';
import { AssetManagementRepository } from '@/features/asset-management/data/asset-management-repository';
import { GetAssetsUseCase } from '@/features/asset-management/use-cases/GetAssetsUseCase';
import { GetAssetDetailUseCase } from '@/features/asset-management/use-cases/GetAssetDetailUseCase';
import { UploadAssetUseCase } from '@/features/asset-management/use-cases/UploadAssetUseCase';
import { DeleteAssetUseCase } from '@/features/asset-management/use-cases/DeleteAssetUseCase';
import { RollbackAssetUseCase } from '@/features/asset-management/use-cases/RollbackAssetUseCase';
// General Settings feature (includes Kiosk Settings tab)
import { GeneralSettingsApi } from '@/features/general-settings/data/general-settings-api';
import { GeneralSettingsRepository } from '@/features/general-settings/data/general-settings-repository';
import { GetGeneralSettingsUseCase } from '@/features/general-settings/use-cases/GetGeneralSettingsUseCase';
import { UpdateGeneralSettingsUseCase } from '@/features/general-settings/use-cases/UpdateGeneralSettingsUseCase';
import { KioskSettingsApi } from '@/features/general-settings/data/kiosk-settings-api';
import { KioskSettingsRepository } from '@/features/general-settings/data/kiosk-settings-repository';
import { GetKioskSettingsUseCase } from '@/features/general-settings/use-cases/GetKioskSettingsUseCase';
import { UpdateKioskSettingsUseCase } from '@/features/general-settings/use-cases/UpdateKioskSettingsUseCase';
// Assign Kiosk - Kiosk Settings tab
import { KioskSettingsApi as AssignKioskSettingsApi } from '@/features/kiosk-settings/data/kiosk-settings-api';
import { KioskSettingsRepository as AssignKioskSettingsRepository } from '@/features/kiosk-settings/data/kiosk-settings-repository';
import { GetKioskSettingsUseCase as GetAssignKioskSettingsUseCase } from '@/features/kiosk-settings/use-cases/GetKioskSettingsUseCase';
import { UpdateKioskSettingsUseCase as UpdateAssignKioskSettingsUseCase } from '@/features/kiosk-settings/use-cases/UpdateKioskSettingsUseCase';
// Languages feature (fetch supported languages for kiosk)
import { LanguagesApi } from '@/features/general-settings/data/languages-api';
import { LanguagesRepository } from '@/features/general-settings/data/languages-repository';
import { GetApplicationLanguagesUseCase as GetKioskApplicationLanguagesUseCase } from '@/features/general-settings/use-cases/GetApplicationLanguagesUseCase';
// Currencies feature (fetch available currencies)
import { CurrenciesApi } from '@/features/general-settings/data/currencies-api';
import { CurrenciesRepository } from '@/features/general-settings/data/currencies-repository';
import { GetCurrenciesUseCase } from '@/features/general-settings/use-cases/GetCurrenciesUseCase';
import { BASE_KIOSK_API } from '@/core/api-config';
// Kiosk Customizer feature
import { KioskConfigApi } from '@/features/device-customizer/kiosk/data/kiosk-config-api';
import { KioskConfigRepository } from '@/features/device-customizer/kiosk/data/kiosk-config-repository';
import { GetKioskConfigUseCase } from '@/features/device-customizer/kiosk/use-cases/GetKioskConfigUseCase';
import { SaveKioskConfigUseCase } from '@/features/device-customizer/kiosk/use-cases/SaveKioskConfigUseCase';
import { UploadKioskIconUseCase } from '@/features/device-customizer/kiosk/use-cases/UploadKioskIconUseCase';
import { ValidateKioskConfigUseCase } from '@/features/device-customizer/kiosk/use-cases/ValidateKioskConfigUseCase';
// Communities feature
import { CommunitiesApi } from '@/features/communities/data/communities-api';
import { CommunitiesRepository } from '@/features/communities/data/communities-repository';
import { GetCommunitiesUseCase } from '@/features/communities/use-cases/GetCommunitiesUseCase';
import { CreateCommunityUseCase } from '@/features/communities/use-cases/CreateCommunityUseCase';
import { UpdateCommunityUseCase } from '@/features/communities/use-cases/UpdateCommunityUseCase';
import { DeleteCommunityUseCase } from '@/features/communities/use-cases/DeleteCommunityUseCase';
// Community Accounts use cases
import { GetCommunityAccountsUseCase } from '@/features/communities/use-cases/GetCommunityAccountsUseCase';
import { UpsertAccountRelationUseCase } from '@/features/communities/use-cases/UpsertAccountRelationUseCase';
// Machine Setup feature
import { MachineSetupApi } from '@/features/machine-setup/data/machine-setup-api';
import { MachineSetupRepository } from '@/features/machine-setup/data/machine-setup-repository';
import { GetMachineTypesUseCase } from '@/features/machine-setup/use-cases/GetMachineTypesUseCase';
import { ValidateSerialUseCase } from '@/features/machine-setup/use-cases/ValidateSerialUseCase';
import { CreateMachineUseCase } from '@/features/machine-setup/use-cases/CreateMachineUseCase';
import { ValidateMachineUseCase } from '@/features/machine-setup/use-cases/ValidateMachineUseCase';
import { GetMachineGroupsUseCase } from '@/features/machine-setup/use-cases/GetMachineGroupsUseCase';
import { GetActorsForOperatorUseCase } from '@/features/machine-setup/use-cases/GetActorsForOperatorUseCase';
import { GetDevicesForActorUseCase } from '@/features/machine-setup/use-cases/GetDevicesForActorUseCase';
import { GetMachineInfoUseCase } from '@/features/machine-setup/use-cases/GetMachineInfoUseCase';
import { ApplyAttributesUseCase } from '@/features/machine-setup/use-cases/ApplyAttributesUseCase';
import { GetTypeSettingsUseCase } from '@/features/machine-setup/use-cases/GetTypeSettingsUseCase';
import { SaveTypeSettingsUseCase } from '@/features/machine-setup/use-cases/SaveTypeSettingsUseCase';
import { AddCustomMachineTypeUseCase } from '@/features/machine-setup/use-cases/AddCustomMachineTypeUseCase';
import { DeleteCustomMachineTypeUseCase } from '@/features/machine-setup/use-cases/DeleteCustomMachineTypeUseCase';
import { GetMachineSetupManufacturersUseCase } from '@/features/machine-setup/use-cases/GetMachineSetupManufacturersUseCase';
import { CreateThirdPartyMachineUseCase } from '@/features/machine-setup/use-cases/CreateThirdPartyMachineUseCase';
import { CreateMachineBulkUseCase } from '@/features/machine-setup/use-cases/CreateMachineBulkUseCase';
// Assign Kiosk
import { AssignKioskApi } from '@/features/assign-kiosk/data/assign-kiosk-api';
import { AssignKioskRepository } from '@/features/assign-kiosk/data/assign-kiosk-repository';
import { GetMachineModelUseCase } from '@/features/assign-kiosk/use-cases/GetMachineModelUseCase';
import { GetKioskDetailsUseCase } from '@/features/assign-kiosk/use-cases/GetKioskDetailsUseCase';
import { GetMachineDefaultCreditUseCase } from '@/features/assign-kiosk/use-cases/GetMachineDefaultCreditUseCase';
import { GetKioskLocationsUseCase } from '@/features/assign-kiosk/use-cases/GetKioskLocationsUseCase';
import { RegisterKioskUseCase } from '@/features/assign-kiosk/use-cases/RegisterKioskUseCase';
import { GetSparkLocationsUseCase } from '@/features/assign-kiosk/use-cases/GetSparkLocationsUseCase';
import { SaveSparkMappingUseCase } from '@/features/assign-kiosk/use-cases/SaveSparkMappingUseCase';
import { GetRemoteStartLocationsUseCase } from '@/features/assign-kiosk/use-cases/GetRemoteStartLocationsUseCase';
import { SaveRemoteStartMappingUseCase } from '@/features/assign-kiosk/use-cases/SaveRemoteStartMappingUseCase';
import { BASE_AUTH_OFFICE_API, BASE_AUTH_OFFICE_API_V2, BASE_ACCOUNTS_API, BASE_S3_URL } from '@/core/api-config';
import { Services, ServicesContainer } from './types';
// Data Error feature
import { KioskFlowApi } from '@/features/kiosk-flow-management/data/kiosk-flow-api';
import { KioskFlowPushApi } from '@/features/kiosk-flow-management/data/kiosk-flow-push-api';
import { KioskFlowRepository } from '@/features/kiosk-flow-management/data/kiosk-flow-repository';
import { GetKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/GetKioskFlowUseCase';
import { CreateKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/CreateKioskFlowUseCase';
import { RenameKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/RenameKioskFlowUseCase';
import { DeleteKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/DeleteKioskFlowUseCase';
import { SaveKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/SaveKioskFlowUseCase';
import { PushKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/PushKioskFlowUseCase';
import { PreparePublishKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/PreparePublishKioskFlowUseCase';
import { PublishKioskFlowUseCase } from '@/features/kiosk-flow-management/use-cases/PublishKioskFlowUseCase';
import { RecordKioskFlowVersionUseCase } from '@/features/kiosk-flow-management/use-cases/RecordKioskFlowVersionUseCase';
import { GetKioskLayoutsUseCase } from '@/features/kiosk-flow-management/use-cases/GetKioskLayoutsUseCase';
import { GetKioskImagesUseCase } from '@/features/kiosk-flow-management/use-cases/GetKioskImagesUseCase';
import { DataErrorApi } from '@/features/data-error/data/data-error-api';
import { DataErrorRepository } from '@/features/data-error/data/data-error-repository';
import { GetIrregularDeltaUseCase } from '@/features/data-error/use-cases/GetIrregularDeltaUseCase';
import { GetIrregularDeltaFiltersUseCase } from '@/features/data-error/use-cases/GetIrregularDeltaFiltersUseCase';
import { GetMeterValuesSurroundUseCase } from '@/features/data-error/use-cases/GetMeterValuesSurroundUseCase';
import { HandleIrregularDeltaUseCase } from '@/features/data-error/use-cases/HandleIrregularDeltaUseCase';
import { BatchHandleIrregularDeltaUseCase } from '@/features/data-error/use-cases/BatchHandleIrregularDeltaUseCase';
import { UpdateDeltaCommentUseCase } from '@/features/data-error/use-cases/UpdateDeltaCommentUseCase';
import { ExportIrregularDeltaUseCase } from '@/features/data-error/use-cases/ExportIrregularDeltaUseCase';
import { RebuildPaymentsUseCase } from '@/features/data-error/use-cases/RebuildPaymentsUseCase';

/**
 * Services container with lazy initialization
 * Creates single instances of all services and manages their dependencies
 */
class DefaultServicesContainer implements ServicesContainer {
  private services: Services | null = null;

  getServices(): Services {
    if (!this.services) {
      this.services = this.createServices();
    }
    return this.services;
  }

  private createServices(): Services {
    // Core infrastructure - created first
    const secureStorage = new LocalSecureStorage();
    const tokenManager = new TokenManager(secureStorage, jwtDecode);
    const authApi = new AuthApi();

    // Auth layer - clean architecture
    const authRepository = new AuthRepository(authApi, tokenManager);
    const initializeAuthUseCase = new InitializeAuthUseCase(authRepository);
    const loginUseCase = new LoginUseCase(authRepository);
    const loginWithTokenUseCase = new LoginWithTokenUseCase(tokenManager);
    const logoutUseCase = new LogoutUseCase(authRepository);

    // Feature APIs - depend on core services
    const actorApi = new ActorApi(BASE_AUTH_OFFICE_API_V2, tokenManager);
    const operatorApi = new OperatorApi(BASE_AUTH_OFFICE_API, tokenManager);
    const kioskSessionApi = new KioskSessionApi(undefined, tokenManager); // Uses default BASE_AUTH_OFFICE_API
    const ocpiAdminApi = new OcpiAdminApi(undefined, tokenManager); // Uses default BASE_OCPI_ADMIN_API
    const manufacturerApi = new ManufacturerApi(BASE_AUTH_OFFICE_API, tokenManager);
    const sessionsReportApi = new SessionsReportApi(BASE_AUTH_OFFICE_API, tokenManager);

    // Repositories - depend on APIs
    const actorRepository = new ActorRepository(actorApi);
    const operatorRepository = new OperatorRepository(operatorApi);
    const kioskSessionRepository = new KioskSessionRepository(kioskSessionApi, tokenManager);
    const ocpiAdminRepository = new OcpiAdminRepository(ocpiAdminApi, tokenManager);
    const manufacturerRepository = new ManufacturerRepository(manufacturerApi);
    const sessionsReportRepository = new SessionsReportRepository(sessionsReportApi);

    // Use Cases - depend on repositories
    const getActorTreeUseCase = new GetActorTreeUseCase(actorRepository);
    const getOperatorMachineListUseCase = new GetOperatorMachineListUseCase(actorRepository);
    const getConnectorCountUseCase = new GetConnectorCountUseCase(actorRepository);
    const getKioskSessionsUseCase = new GetKioskSessionsUseCase(kioskSessionRepository);
    const checkConnectionStatusUseCase = new CheckConnectionStatusUseCase(ocpiAdminRepository);
    const publishLocationsUseCase = new PublishLocationsUseCase(ocpiAdminRepository);
    const publishTariffsUseCase = new PublishTariffsUseCase(ocpiAdminRepository);
    const setupKioskConnectionUseCase = new SetupKioskConnectionUseCase(ocpiAdminRepository);
    const onboardOperatorUseCase = new OnboardOperatorUseCase(ocpiAdminRepository);
    const setupExternalCpoConnectionUseCase = new SetupExternalCpoConnectionUseCase(ocpiAdminRepository);
    const setupVpostConnectionUseCase = new SetupVpostConnectionUseCase(ocpiAdminRepository);
    const getCsmsNamesUseCase = new GetCsmsNamesUseCase(ocpiAdminRepository);
    const getKioskCsmsNamesUseCase = new GetKioskCsmsNamesUseCase(ocpiAdminRepository);
    const syncEvesUseCase = new SyncEvesUseCase(ocpiAdminRepository);
    const syncCpoOcpiDataUseCase = new SyncCpoOcpiDataUseCase(ocpiAdminRepository);
    // Manufacturer use cases
    const getManufacturersUseCase = new GetManufacturersUseCase(manufacturerRepository);
    const getManufacturerByIdUseCase = new GetManufacturerByIdUseCase(manufacturerRepository);
    const createManufacturerUseCase = new CreateManufacturerUseCase(manufacturerRepository);
    const updateManufacturerUseCase = new UpdateManufacturerUseCase(manufacturerRepository);
    const deleteManufacturerUseCase = new DeleteManufacturerUseCase(manufacturerRepository);
    // Sessions Report use cases
    const getSessionsUseCase = new GetSessionsUseCase(sessionsReportRepository);
    const getMeterValuesUseCase = new GetMeterValuesUseCase(sessionsReportRepository);
    const getTariffLevelsUseCase = new GetTariffLevelsUseCase(sessionsReportRepository);

    // Operator Dashboard feature
    const dashboardApi = new DashboardApi(BASE_AUTH_OFFICE_API, tokenManager);
    const dashboardRepository = new DashboardRepository(dashboardApi);
    const getFleetSummaryUseCase = new GetFleetSummaryUseCase(dashboardRepository);
    const getFleetStatusUseCase = new GetFleetStatusUseCase(dashboardRepository);
    const getFleetMapUseCase = new GetFleetMapUseCase(dashboardRepository);
    const getAnalyticsUseCase = new GetAnalyticsUseCase(dashboardRepository);
    const getActiveSessionsUseCase = new GetActiveSessionsUseCase(dashboardRepository);
    const getRecentSessionsUseCase = new GetRecentSessionsUseCase(dashboardRepository);
    const getAlertsUseCase = new GetAlertsUseCase(dashboardRepository);
    const getAccountsUseCase = new GetAccountsUseCase(dashboardRepository);

    // Translation Management feature
    const translationApi = new TranslationApi(BASE_AUTH_OFFICE_API, tokenManager);
    const translationRepository = new TranslationRepository(translationApi);
    const getTranslationsUseCase = new GetTranslationsUseCase(translationRepository);
    const getAllTranslationsUseCase = new GetAllTranslationsUseCase(translationRepository);
    const createTranslationUseCase = new CreateTranslationUseCase(translationRepository);
    const updateTranslationUseCase = new UpdateTranslationUseCase(translationRepository);
    const deleteTranslationUseCase = new DeleteTranslationUseCase(translationRepository);
    const getLanguagesUseCase = new GetLanguagesUseCase(translationRepository);
    const validateBulkImportUseCase = new ValidateBulkImportUseCase(translationRepository);
    const bulkImportUseCase = new BulkImportUseCase(translationRepository);
    const downloadTemplateUseCase = new DownloadTemplateUseCase(translationRepository);
    const exportTranslationsUseCase = new ExportTranslationsUseCase(translationRepository);
    const getPublishStatusUseCase = new GetPublishStatusUseCase(translationRepository);
    const publishTranslationsUseCase = new PublishTranslationsUseCase(translationRepository);
    const getVersionsUseCase = new GetVersionsUseCase(translationRepository);
    const getVersionContentUseCase = new GetVersionContentUseCase(translationRepository);
    const rollbackVersionUseCase = new RollbackVersionUseCase(translationRepository);
    const getPreviewInfoUseCase = new GetPreviewInfoUseCase(translationRepository);
    const getPreviewContentUseCase = new GetPreviewContentUseCase(translationRepository);
    const downloadPreviewUseCase = new DownloadPreviewUseCase(translationRepository);
    const copyLanguageValuesUseCase = new CopyLanguageValuesUseCase(translationRepository);

    // Language Management feature
    const languageManagementApi = new LanguageManagementApi(BASE_AUTH_OFFICE_API, tokenManager);
    const languageManagementRepository = new LanguageManagementRepository(languageManagementApi);
    const getAllLanguagesUseCase = new GetAllLanguagesUseCase(languageManagementRepository);
    const createLanguageUseCase = new CreateLanguageUseCase(languageManagementRepository);
    const updateLanguageUseCase = new UpdateLanguageUseCase(languageManagementRepository);
    const deleteLanguageUseCase = new DeleteLanguageUseCase(languageManagementRepository);
    const getApplicationLanguagesUseCase = new GetApplicationLanguagesUseCase(languageManagementRepository);
    const setApplicationLanguagesUseCase = new SetApplicationLanguagesUseCase(languageManagementRepository);
    const batchReorderLanguagesUseCase = new BatchReorderLanguagesUseCase(languageManagementRepository);

    // Sound Management feature
    const soundManagementApi = new SoundManagementApi(BASE_AUTH_OFFICE_API, tokenManager);
    const soundManagementRepository = new SoundManagementRepository(soundManagementApi);
    const getSoundsUseCase = new GetSoundsUseCase(soundManagementRepository);
    const uploadSoundUseCase = new UploadSoundUseCase(soundManagementRepository);
    const deleteSoundUseCase = new DeleteSoundUseCase(soundManagementRepository);
    const copySoundsUseCase = new CopySoundsUseCase(soundManagementRepository);

    // Import Configuration feature
    const importConfigurationApi = new ImportConfigurationApi(BASE_AUTH_OFFICE_API, tokenManager);
    const importConfigurationRepository = new ImportConfigurationRepository(importConfigurationApi);
    const getSheetMappingsUseCase = new GetSheetMappingsUseCase(importConfigurationRepository);
    const getSheetMappingDetailUseCase = new GetSheetMappingDetailUseCase(importConfigurationRepository);
    const saveSheetMappingUseCase = new SaveSheetMappingUseCase(importConfigurationRepository);
    const deleteSheetMappingUseCase = new DeleteSheetMappingUseCase(importConfigurationRepository);
    const saveColumnMappingUseCase = new SaveColumnMappingUseCase(importConfigurationRepository);
    const deleteColumnMappingUseCase = new DeleteColumnMappingUseCase(importConfigurationRepository);

    // API Tokens feature
    const apiTokensApi = new ApiTokensApi(BASE_AUTH_OFFICE_API, tokenManager);

    // Asset Management feature
    const assetManagementApi = new AssetManagementApi(BASE_AUTH_OFFICE_API, tokenManager);
    const assetManagementRepository = new AssetManagementRepository(assetManagementApi, BASE_S3_URL);
    const getAssetsUseCase = new GetAssetsUseCase(assetManagementRepository);
    const getAssetDetailUseCase = new GetAssetDetailUseCase(assetManagementRepository);
    const uploadAssetUseCase = new UploadAssetUseCase(assetManagementRepository);
    const deleteAssetUseCase = new DeleteAssetUseCase(assetManagementRepository);
    const rollbackAssetUseCase = new RollbackAssetUseCase(assetManagementRepository);

    // Communities feature
    const communitiesApi = new CommunitiesApi(BASE_ACCOUNTS_API, tokenManager);
    const communitiesRepository = new CommunitiesRepository(communitiesApi);
    const getCommunitiesUseCase = new GetCommunitiesUseCase(communitiesRepository);
    const createCommunityUseCase = new CreateCommunityUseCase(communitiesRepository);
    const updateCommunityUseCase = new UpdateCommunityUseCase(communitiesRepository);
    const deleteCommunityUseCase = new DeleteCommunityUseCase(communitiesRepository);

    // Community Accounts use cases (use CommunitiesRepository)
    const getCommunityAccountsUseCase = new GetCommunityAccountsUseCase(communitiesRepository);
    const upsertAccountRelationUseCase = new UpsertAccountRelationUseCase(communitiesRepository);

    // Machine Setup feature
    const machineSetupApi = new MachineSetupApi(BASE_AUTH_OFFICE_API, tokenManager);
    const machineSetupRepository = new MachineSetupRepository(machineSetupApi);
    const getMachineTypesUseCase = new GetMachineTypesUseCase(machineSetupRepository);
    const validateSerialUseCase = new ValidateSerialUseCase(machineSetupRepository);
    const createMachineUseCase = new CreateMachineUseCase(machineSetupRepository);
    const validateMachineUseCase = new ValidateMachineUseCase(machineSetupRepository);
    const getMachineGroupsUseCase = new GetMachineGroupsUseCase(machineSetupRepository);
    const getActorsForOperatorUseCase = new GetActorsForOperatorUseCase(machineSetupRepository);
    const getDevicesForActorUseCase = new GetDevicesForActorUseCase(machineSetupRepository);
    const getMachineInfoUseCase = new GetMachineInfoUseCase(machineSetupRepository);
    const applyAttributesUseCase = new ApplyAttributesUseCase(machineSetupRepository);
    const getTypeSettingsUseCase = new GetTypeSettingsUseCase(machineSetupRepository);
    const saveTypeSettingsUseCase = new SaveTypeSettingsUseCase(machineSetupRepository);
    const addCustomMachineTypeUseCase = new AddCustomMachineTypeUseCase(machineSetupRepository);
    const deleteCustomMachineTypeUseCase = new DeleteCustomMachineTypeUseCase(machineSetupRepository);
    const getMachineSetupManufacturersUseCase = new GetMachineSetupManufacturersUseCase(machineSetupRepository);
    const createThirdPartyMachineUseCase = new CreateThirdPartyMachineUseCase(machineSetupRepository);
    const createMachineBulkUseCase = new CreateMachineBulkUseCase(machineSetupRepository);

    // General Settings feature (DCS Apps - General tab)
    const generalSettingsApi = new GeneralSettingsApi(BASE_AUTH_OFFICE_API, tokenManager);
    const generalSettingsRepository = new GeneralSettingsRepository(generalSettingsApi);
    const getGeneralSettingsUseCase = new GetGeneralSettingsUseCase(generalSettingsRepository);
    const updateGeneralSettingsUseCase = new UpdateGeneralSettingsUseCase(generalSettingsRepository);

    // Kiosk Settings feature (Kiosk tab - within general-settings)
    const kioskSettingsApi = new KioskSettingsApi(BASE_KIOSK_API, tokenManager);
    const kioskSettingsRepository = new KioskSettingsRepository(kioskSettingsApi);
    const getKioskSettingsUseCase = new GetKioskSettingsUseCase(kioskSettingsRepository);
    const updateKioskSettingsUseCase = new UpdateKioskSettingsUseCase(kioskSettingsRepository);

    // Assign Kiosk - Kiosk Settings tab
    const assignKioskSettingsApi = new AssignKioskSettingsApi(BASE_KIOSK_API, tokenManager);
    const assignKioskSettingsRepository = new AssignKioskSettingsRepository(assignKioskSettingsApi);
    const getAssignKioskSettingsUseCase = new GetAssignKioskSettingsUseCase(assignKioskSettingsRepository);
    const updateAssignKioskSettingsUseCase = new UpdateAssignKioskSettingsUseCase(assignKioskSettingsRepository);

    // Languages feature (fetch supported languages for kiosk)
    const languagesApi = new LanguagesApi(BASE_AUTH_OFFICE_API, tokenManager);
    const languagesRepository = new LanguagesRepository(languagesApi);
    const getKioskApplicationLanguagesUseCase = new GetKioskApplicationLanguagesUseCase(languagesRepository);

    // Currencies feature (fetch available currencies)
    const currenciesApi = new CurrenciesApi(BASE_AUTH_OFFICE_API, tokenManager);
    const currenciesRepository = new CurrenciesRepository(currenciesApi);
    const getCurrenciesUseCase = new GetCurrenciesUseCase(currenciesRepository);

    // Data Error feature
    const dataErrorApi = new DataErrorApi(BASE_AUTH_OFFICE_API, tokenManager);
    const dataErrorRepository = new DataErrorRepository(dataErrorApi);
    const getIrregularDeltaUseCase = new GetIrregularDeltaUseCase(dataErrorRepository);
    const getIrregularDeltaFiltersUseCase = new GetIrregularDeltaFiltersUseCase(dataErrorRepository);
    const getMeterValuesSurroundUseCase = new GetMeterValuesSurroundUseCase(dataErrorRepository);
    const handleIrregularDeltaUseCase = new HandleIrregularDeltaUseCase(dataErrorRepository);
    const batchHandleIrregularDeltaUseCase = new BatchHandleIrregularDeltaUseCase(dataErrorRepository);
    const updateDeltaCommentUseCase = new UpdateDeltaCommentUseCase(dataErrorRepository);
    const exportIrregularDeltaUseCase = new ExportIrregularDeltaUseCase(dataErrorRepository);
    const rebuildPaymentsUseCase = new RebuildPaymentsUseCase(dataErrorRepository);

    // Kiosk Flow Management feature
    const kioskFlowApi = new KioskFlowApi(BASE_AUTH_OFFICE_API, tokenManager);
    const kioskFlowPushApi = new KioskFlowPushApi(BASE_KIOSK_API, tokenManager);
    const kioskFlowRepository = new KioskFlowRepository(kioskFlowApi, kioskFlowPushApi);
    const getKioskFlowUseCase = new GetKioskFlowUseCase(kioskFlowRepository);
    const createKioskFlowUseCase = new CreateKioskFlowUseCase(kioskFlowRepository);
    const renameKioskFlowUseCase = new RenameKioskFlowUseCase(kioskFlowRepository);
    const deleteKioskFlowUseCase = new DeleteKioskFlowUseCase(kioskFlowRepository);
    const saveKioskFlowUseCase = new SaveKioskFlowUseCase(kioskFlowRepository);
    const getKioskLayoutsUseCase = new GetKioskLayoutsUseCase(kioskFlowRepository);
    const getKioskImagesUseCase = new GetKioskImagesUseCase(kioskFlowRepository);
    const pushKioskFlowUseCase = new PushKioskFlowUseCase(kioskFlowRepository);
    const preparePublishKioskFlowUseCase = new PreparePublishKioskFlowUseCase(kioskFlowRepository);
    const publishKioskFlowUseCase = new PublishKioskFlowUseCase(kioskFlowRepository, pushKioskFlowUseCase);
    const recordKioskFlowVersionUseCase = new RecordKioskFlowVersionUseCase(kioskFlowRepository);

    // Assign Kiosk
    const assignKioskApi = new AssignKioskApi(BASE_AUTH_OFFICE_API, BASE_KIOSK_API, tokenManager);
    const assignKioskRepository = new AssignKioskRepository(assignKioskApi);
    const getMachineModelUseCase = new GetMachineModelUseCase(assignKioskRepository);
    const getKioskDetailsUseCase = new GetKioskDetailsUseCase(assignKioskRepository);
    const getMachineDefaultCreditUseCase = new GetMachineDefaultCreditUseCase(assignKioskRepository);
    const getKioskLocationsUseCase = new GetKioskLocationsUseCase(assignKioskRepository);
    const registerKioskUseCase = new RegisterKioskUseCase(assignKioskRepository);
    const getSparkLocationsUseCase = new GetSparkLocationsUseCase(assignKioskRepository);
    const saveSparkMappingUseCase = new SaveSparkMappingUseCase(assignKioskRepository);
    const getRemoteStartLocationsUseCase = new GetRemoteStartLocationsUseCase(assignKioskRepository);
    const saveRemoteStartMappingUseCase = new SaveRemoteStartMappingUseCase(assignKioskRepository);

    return {
      // Core
      secureStorage,
      tokenManager,
      authApi,

      // Auth layer
      authRepository,
      initializeAuthUseCase,
      loginUseCase,
      loginWithTokenUseCase,
      logoutUseCase,

      // APIs
      actorApi,
      operatorApi,
      kioskSessionApi,
      ocpiAdminApi,

      // Repositories
      actorRepository,
      operatorRepository,
      kioskSessionRepository,
      ocpiAdminRepository,

      // Use Cases
      getActorTreeUseCase,
      getOperatorMachineListUseCase,
      getConnectorCountUseCase,
      getKioskSessionsUseCase,
      checkConnectionStatusUseCase,
      publishLocationsUseCase,
      publishTariffsUseCase,
      setupKioskConnectionUseCase,
      onboardOperatorUseCase,
      setupExternalCpoConnectionUseCase,
      setupVpostConnectionUseCase,
      getCsmsNamesUseCase,
      getKioskCsmsNamesUseCase,
      syncEvesUseCase,
      syncCpoOcpiDataUseCase,

      // Manufacturer feature
      manufacturerApi,
      manufacturerRepository,
      getManufacturersUseCase,
      getManufacturerByIdUseCase,
      createManufacturerUseCase,
      updateManufacturerUseCase,
      deleteManufacturerUseCase,

      // Sessions Report feature
      sessionsReportApi,
      sessionsReportRepository,
      getSessionsUseCase,
      getMeterValuesUseCase,
      getTariffLevelsUseCase,

      // Operator Dashboard feature
      dashboardApi,
      dashboardRepository,
      getFleetSummaryUseCase,
      getFleetStatusUseCase,
      getFleetMapUseCase,
      getAnalyticsUseCase,
      getActiveSessionsUseCase,
      getRecentSessionsUseCase,
      getAlertsUseCase,
      getAccountsUseCase,

      // Translation Management feature
      translationApi,
      translationRepository,
      getTranslationsUseCase,
      getAllTranslationsUseCase,
      createTranslationUseCase,
      updateTranslationUseCase,
      deleteTranslationUseCase,
      getLanguagesUseCase,
      validateBulkImportUseCase,
      bulkImportUseCase,
      downloadTemplateUseCase,
      exportTranslationsUseCase,
      getPublishStatusUseCase,
      publishTranslationsUseCase,
      getVersionsUseCase,
      getVersionContentUseCase,
      rollbackVersionUseCase,
      getPreviewInfoUseCase,
      getPreviewContentUseCase,
      downloadPreviewUseCase,
      copyLanguageValuesUseCase,

      // Language Management feature
      languageManagementApi,
      languageManagementRepository,
      getAllLanguagesUseCase,
      createLanguageUseCase,
      updateLanguageUseCase,
      deleteLanguageUseCase,
      getApplicationLanguagesUseCase,
      setApplicationLanguagesUseCase,
      batchReorderLanguagesUseCase,

      // Sound Management feature
      soundManagementApi,
      soundManagementRepository,
      getSoundsUseCase,
      uploadSoundUseCase,
      deleteSoundUseCase,
      copySoundsUseCase,

      // Import Configuration feature
      importConfigurationApi,
      importConfigurationRepository,
      getSheetMappingsUseCase,
      getSheetMappingDetailUseCase,
      saveSheetMappingUseCase,
      deleteSheetMappingUseCase,
      saveColumnMappingUseCase,
      deleteColumnMappingUseCase,

      // API Tokens feature
      apiTokensApi,

      // Asset Management feature
      assetManagementApi,
      assetManagementRepository,
      getAssetsUseCase,
      getAssetDetailUseCase,
      uploadAssetUseCase,
      deleteAssetUseCase,
      rollbackAssetUseCase,

      // Kiosk Settings feature (General Settings)
      kioskSettingsApi,
      kioskSettingsRepository,
      getKioskSettingsUseCase,
      updateKioskSettingsUseCase,

      // Assign Kiosk - Kiosk Settings tab
      assignKioskSettingsApi,
      assignKioskSettingsRepository,
      getAssignKioskSettingsUseCase,
      updateAssignKioskSettingsUseCase,

      // Languages feature (fetch supported languages for kiosk)
      languagesApi,
      languagesRepository,
      getKioskApplicationLanguagesUseCase,

      // Currencies feature (fetch available currencies)
      currenciesApi,
      currenciesRepository,
      getCurrenciesUseCase,

      // General Settings feature
      generalSettingsApi,
      generalSettingsRepository,
      getGeneralSettingsUseCase,
      updateGeneralSettingsUseCase,

      // Kiosk Customizer feature
      kioskConfigApi: new KioskConfigApi(undefined, tokenManager),
      kioskConfigRepository: new KioskConfigRepository(
        new KioskConfigApi(undefined, tokenManager)
      ),
      getKioskConfigUseCase: new GetKioskConfigUseCase(
        new KioskConfigRepository(new KioskConfigApi(undefined, tokenManager))
      ),
      saveKioskConfigUseCase: new SaveKioskConfigUseCase(
        new KioskConfigRepository(new KioskConfigApi(undefined, tokenManager))
      ),
      uploadKioskIconUseCase: new UploadKioskIconUseCase(assetManagementRepository),
      validateKioskConfigUseCase: new ValidateKioskConfigUseCase(),

      // Communities feature
      communitiesApi,
      communitiesRepository,
      getCommunitiesUseCase,
      createCommunityUseCase,
      updateCommunityUseCase,
      deleteCommunityUseCase,

      // Community Accounts use cases
      getCommunityAccountsUseCase,
      upsertAccountRelationUseCase,

      // Machine Setup feature
      getMachineTypesUseCase,
      validateSerialUseCase,
      createMachineUseCase,
      validateMachineUseCase,
      getMachineGroupsUseCase,
      getActorsForOperatorUseCase,
      getDevicesForActorUseCase,
      getMachineInfoUseCase,
      applyAttributesUseCase,
      getTypeSettingsUseCase,
      saveTypeSettingsUseCase,
      addCustomMachineTypeUseCase,
      deleteCustomMachineTypeUseCase,
      getMachineSetupManufacturersUseCase,
      createThirdPartyMachineUseCase,
      createMachineBulkUseCase,

      // Data Error feature
      getIrregularDeltaUseCase,
      getIrregularDeltaFiltersUseCase,
      getMeterValuesSurroundUseCase,
      handleIrregularDeltaUseCase,
      batchHandleIrregularDeltaUseCase,
      updateDeltaCommentUseCase,
      exportIrregularDeltaUseCase,
      rebuildPaymentsUseCase,

      // Kiosk Flow Management feature
      getKioskFlowUseCase,
      createKioskFlowUseCase,
      renameKioskFlowUseCase,
      deleteKioskFlowUseCase,
      saveKioskFlowUseCase,
      getKioskLayoutsUseCase,
      getKioskImagesUseCase,
      pushKioskFlowUseCase,
      preparePublishKioskFlowUseCase,
      publishKioskFlowUseCase,
      recordKioskFlowVersionUseCase,

      // Assign Kiosk
      assignKioskApi,
      assignKioskRepository,
      getMachineModelUseCase,
      getKioskDetailsUseCase,
      getMachineDefaultCreditUseCase,
      getKioskLocationsUseCase,
      registerKioskUseCase,
      getSparkLocationsUseCase,
      saveSparkMappingUseCase,
      getRemoteStartLocationsUseCase,
      saveRemoteStartMappingUseCase,
    };
  }
}

/**
 * Global singleton container instance
 * This ensures we only ever create one instance of each service
 */
export const servicesContainer = new DefaultServicesContainer();
